// Arduino Uno R3 en 3D.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, glow, setGlow, emissiveMat, setEmissive } from '../kit.js';
import { UNO } from '../../parts/boards.js';
import { getPins } from '../../parts/registry.js';
import { usbCable } from './usb.js';

const W = UNO.W;
const D = UNO.D;
const TOP = UNO.PCB_TOP;
const TEAL = '#0b7d8e';

let silkTex = null;

export function rand(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function drawSilk(pins) {
  const { c, g } = canvas(W, D, 22);
  const X = (x) => x + W / 2;
  const Y = (z) => z + D / 2;
  g.fillStyle = TEAL;
  g.fillRect(0, 0, W, D);
  // Pistas de cobre bajo la máscara
  const r = rand(328);
  g.strokeStyle = 'rgba(4,70,82,0.55)';
  g.lineWidth = 0.28;
  g.lineCap = 'round';
  for (let i = 0; i < 70; i++) {
    let x = 4 + r() * (W - 8);
    let y = 4 + r() * (D - 8);
    g.beginPath();
    g.moveTo(x, y);
    for (let k = 0; k < 3; k++) {
      const horiz = r() > 0.5;
      const len = 3 + r() * 12;
      if (horiz) x += r() > 0.5 ? len : -len;
      else y += r() > 0.5 ? len : -len;
      x = Math.max(2, Math.min(W - 2, x));
      y = Math.max(2, Math.min(D - 2, y));
      g.lineTo(x, y);
    }
    g.stroke();
  }
  g.fillStyle = 'rgba(190,225,228,0.55)';
  for (let i = 0; i < 40; i++) {
    g.beginPath();
    g.arc(3 + r() * (W - 6), 3 + r() * (D - 6), 0.32, 0, Math.PI * 2);
    g.fill();
  }

  const white = '#f3f6f6';
  g.strokeStyle = white;
  g.lineWidth = 0.18;
  const byName = Object.fromEntries(pins.map((p) => [p.name, p]));
  const outline = (names, extra = 0) => {
    const xs = names.map((n) => byName[n].x);
    const z = byName[names[0]].z;
    const x0 = Math.min(...xs) - 1.27 - extra;
    const x1 = Math.max(...xs) + 1.27;
    g.strokeRect(X(x0) - 0.3, Y(z) - 1.6, x1 - x0 + 0.6, 3.2);
  };
  outline(['SCL', 'SDA', 'AREF', 'GND.1', '13', '12', '11', '10', '9', '8']);
  outline(['7', '6', '5', '4', '3', '2', '1', '0']);
  outline(['IOREF', 'RESET', '3.3V', '5V', 'GND.2', 'GND.3', 'VIN'], 2.54);
  outline(['A0', 'A1', 'A2', 'A3', 'A4', 'A5']);

  const top = { SCL: 'SCL', SDA: 'SDA', AREF: 'AREF', 'GND.1': 'GND', 1: 'TX▸1', 0: 'RX◂0' };
  for (const p of pins) {
    if (p.z < 0) {
      const label = top[p.name] || (['3', '5', '6', '9', '10', '11'].includes(p.name) ? '~' + p.name : p.name);
      text(g, label, X(p.x), Y(p.z) + 2.1, { size: 1.15, color: white, align: 'right', rotate: -Math.PI / 2, weight: 700 });
    } else {
      const label = { 'GND.2': 'GND', 'GND.3': 'GND', '3.3V': '3.3V', VIN: 'Vin' }[p.name] || p.name;
      text(g, label, X(p.x), Y(p.z) - 2.1, { size: 1.15, color: white, align: 'left', rotate: -Math.PI / 2, weight: 700 });
    }
  }
  text(g, 'DIGITAL (PWM~)', X(13), Y(-14.6), { size: 1.7, color: white, weight: 700 });
  text(g, 'POWER', X(0), Y(16.4), { size: 1.5, color: white, weight: 700 });
  text(g, 'ANALOG IN', X(20.3), Y(16.4), { size: 1.5, color: white, weight: 700 });
  g.fillRect(X(-9), Y(15.0), 18, 0.25);
  g.fillRect(X(12.7), Y(15.0), 15.2, 0.25);

  // Logotipo
  const lx = X(20.5);
  const ly = Y(-5.6);
  g.lineWidth = 0.9;
  g.beginPath();
  g.ellipse(lx - 3.1, ly, 2.6, 2.2, 0, 0, Math.PI * 2);
  g.ellipse(lx + 3.1, ly, 2.6, 2.2, 0, 0, Math.PI * 2);
  g.stroke();
  g.fillRect(lx - 4.3, ly - 0.3, 2.4, 0.6);
  g.fillRect(lx + 1.9, ly - 0.3, 2.4, 0.6);
  g.fillRect(lx + 2.8, ly - 1.2, 0.6, 2.4);
  text(g, 'UNO', X(19.8), Y(1.6), { size: 6.2, color: white, weight: 800 });
  text(g, 'ARDUINO', X(20.5), Y(-1.9), { size: 1.9, color: white, weight: 800 });
  text(g, 'ON', X(27.6), Y(-9.6), { size: 1.2, color: white, weight: 700 });
  text(g, 'L', X(-7.7), Y(-14.2), { size: 1.2, color: white, weight: 700 });
  text(g, 'TX', X(-8.1), Y(-11.7), { size: 1.2, color: white, weight: 700 });
  text(g, 'RX', X(-8.1), Y(-9.5), { size: 1.2, color: white, weight: 700 });
  text(g, 'RESET', X(-25.9), Y(-17.6), { size: 1.1, color: white, weight: 700 });
  text(g, 'ICSP', X(29.5), Y(5.2), { size: 1.1, color: white, weight: 700, rotate: -Math.PI / 2 });
  text(g, 'ICSP', X(-2.5), Y(-13.2), { size: 1.0, color: white, weight: 700 });
  text(g, 'MADE IN ITALY', X(3.5), Y(9.6), { size: 1.0, color: white, weight: 600 });
  text(g, '1', X(30.8), Y(-3.9), { size: 0.9, color: white });
  g.strokeRect(X(26.9), Y(-3.8), 5.1, 7.6);
  const tex = texture(c);
  tex.userData.shared = true;
  return tex;
}

export function chipLabel(label, w, d) {
  const { c, g } = canvas(w, d, 18);
  g.fillStyle = '#1c1c1f';
  g.fillRect(0, 0, w, d);
  text(g, label, w / 2, d / 2 - 0.7, { size: 1.5, color: '#9a9ca1', weight: 600 });
  text(g, 'Atmel  1834', w / 2, d / 2 + 1.4, { size: 1.0, color: '#7c7e83', weight: 500 });
  g.fillStyle = '#121214';
  g.beginPath();
  g.arc(0.2, d / 2, 0.9, -Math.PI / 2, Math.PI / 2);
  g.fill();
  g.beginPath();
  g.arc(2.2, d - 1.2, 0.45, 0, Math.PI * 2);
  g.fill();
  const t = texture(c);
  t.userData.shared = true;
  return t;
}

let chipTex = null;

/** Header hembra: caja negra con agujeros cuadrados. */
export function femaleHeader(b, xs, z, y, { h = 8.5, pitch = 2.54 } = {}) {
  const x0 = Math.min(...xs) - pitch / 2;
  const x1 = Math.max(...xs) + pitch / 2;
  b.box(x1 - x0, h, pitch, M.blackPlastic, (x0 + x1) / 2, y, z);
  for (const x of xs) {
    b.box(1.05, 0.06, 1.05, M.hole, x, y + h, z);
    b.box(1.5, 0.04, 1.5, mat('#26272a', { roughness: 0.5 }), x, y + h - 0.02, z);
  }
}

/** Header macho de 2 filas (ICSP). */
export function maleHeader2(b, cx, cz, cols, rows, y, { rotate = false } = {}) {
  const p = 2.54;
  const w = (rotate ? rows : cols) * p;
  const d = (rotate ? cols : rows) * p;
  b.box(w, 2.5, d, M.blackPlastic, cx, y, cz);
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const x = cx + (rotate ? (j - (rows - 1) / 2) * p : (i - (cols - 1) / 2) * p);
      const z = cz + (rotate ? (i - (cols - 1) / 2) * p : (j - (rows - 1) / 2) * p);
      b.box(0.64, 6, 0.64, M.gold, x, y + 0.5, z);
    }
  }
}

export function buildUno(def, props) {
  const pins = getPins(def.type, props);
  if (!silkTex) silkTex = drawSilk(pins);
  if (!chipTex) chipTex = chipLabel('ATMEGA328P-PU', 35, 7.2);
  const b = new Builder();
  const holes = [
    [(550 - 1350) * 0.0254, -(100 - 1050) * 0.0254, 1.6],
    [(600 - 1350) * 0.0254, -(2000 - 1050) * 0.0254, 1.6],
    [(2600 - 1350) * 0.0254, -(1400 - 1050) * 0.0254, 1.6],
    [(2600 - 1350) * 0.0254, -(300 - 1050) * 0.0254, 1.6],
  ];
  const pcb = extrudeBoard(boardShape(W, D, { r: 1.2, chamfer: { tr: 2.4, br: 2.4 } }), UNO.PCB_T, silkTex, '#0a6d7c', { holes, w: W, d: D, y0: UNO.PCB_Y });
  b.root.add(pcb);
  for (const [x, z] of holes) {
    b.add(new THREE.RingGeometry(1.6, 2.7, 24).rotateX(-Math.PI / 2), M.tin, { x, y: TOP + 0.02, z });
    b.cyl(2.6, 2.6, UNO.PCB_Y, M.black, x, 0, z, 16);
  }

  // Headers hembra
  const byName = Object.fromEntries(pins.map((p) => [p.name, p]));
  const xs = (names) => names.map((n) => byName[n].x);
  femaleHeader(b, xs(['SCL', 'SDA', 'AREF', 'GND.1', '13', '12', '11', '10', '9', '8']), byName['8'].z, TOP);
  femaleHeader(b, xs(['7', '6', '5', '4', '3', '2', '1', '0']), byName['0'].z, TOP);
  femaleHeader(b, [byName.IOREF.x - 2.54, ...xs(['IOREF', 'RESET', '3.3V', '5V', 'GND.2', 'GND.3', 'VIN'])], byName.VIN.z, TOP);
  femaleHeader(b, xs(['A0', 'A1', 'A2', 'A3', 'A4', 'A5']), byName.A0.z, TOP);

  // USB tipo B
  const usbZ = -11.43;
  b.box(16, 10.9, 12, M.metal, -30.48, TOP, usbZ);
  b.box(0.2, 7.6, 8.4, M.hole, -38.5, TOP + 1.6, usbZ);
  b.box(4, 3.2, 5.6, mat('#e9e7df', { roughness: 0.6 }), -36.6, TOP + 3.8, usbZ);
  // Conector de alimentación
  b.box(13.5, 11, 9, M.black, -28.6, TOP, 18.4);
  b.add(new THREE.CylinderGeometry(3.2, 3.2, 0.4, 24), M.hole, { x: -35.4, y: TOP + 6.2, z: 18.4, rz: Math.PI / 2 });
  b.cylX(1.0, 6, M.metal, -33.5, TOP + 6.2, 18.4, 12);

  // ATmega328P en zócalo
  const cx = 15.24;
  const cz = 7.62;
  b.box(36.5, 2.2, 10, mat('#141416', { roughness: 0.6 }), cx, TOP, cz);
  b.box(35.2, 3.4, 7.3, M.chip, cx, TOP + 2.2, cz);
  const lbl = new THREE.Mesh(new THREE.PlaneGeometry(35, 7.2).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ map: chipTex, roughness: 0.55 }));
  lbl.position.set(cx, TOP + 5.62, cz);
  lbl.receiveShadow = true;
  b.root.add(lbl);
  for (let i = 0; i < 14; i++) {
    const x = cx - 16.51 + i * 2.54;
    for (const s of [-1, 1]) {
      b.box(0.55, 2.8, 1.4, M.tin, x, TOP + 2.0, cz + s * 4.15);
      b.box(0.55, 0.35, 0.9, M.tin, x, TOP + 4.45, cz + s * 3.6);
    }
  }
  // ATmega16U2, cristal, regulador y capacitores
  b.box(5, 0.9, 5, M.chip, -16.2, TOP, -10.9, { ry: Math.PI / 4 });
  b.rbox(11, 3.6, 4.6, 1.6, M.steel, -11.5, TOP, -2.6);
  b.box(6.5, 1.7, 3.6, M.chip, -21.4, TOP, 12.4);
  b.box(3, 0.4, 1.6, M.tin, -21.4, TOP, 10.0);
  for (const x of [-16.6, -10.5]) {
    b.cyl(3.15, 3.15, 5.6, mat('#1d2a3d', { roughness: 0.5 }), x, TOP, 19.6, 24);
    b.cyl(3.0, 3.0, 0.3, M.metal, x, TOP + 5.6, 19.6, 24);
  }
  b.cyl(1.9, 1.9, 4.2, mat('#2f2b28', { roughness: 0.5 }), 4.6, TOP, 3.4, 16);
  // ICSP
  maleHeader2(b, 29.45, 0, 2, 3, TOP, { rotate: true });
  maleHeader2(b, -2.5, -17, 2, 3, TOP, { rotate: true });
  // Componentes SMD de adorno
  const r = rand(16);
  const smd = [mat('#2b2722', { roughness: 0.5 }), mat('#b8a07a', { roughness: 0.6 }), M.chip];
  for (let i = 0; i < 26; i++) {
    let x = -30 + r() * 62;
    let z = -6 + r() * 26;
    if (x > -4 && x < 34 && z > 1 && z < 14) x -= 12;
    const rot = r() > 0.5 ? Math.PI / 2 : 0;
    b.box(1.6, 0.55, 0.8, smd[i % 3], x, TOP, z, { ry: rot });
  }
  // Botón RESET (base fija + tapa interactiva)
  b.box(6, 2.2, 6, M.metal, -25.9, TOP, -21.6);
  b.box(4.6, 0.6, 4.6, M.black, -25.9, TOP + 2.2, -21.6);
  b.build();

  const cap = new THREE.Mesh(new THREE.CylinderGeometry(1.9, 1.9, 1.5, 24), mat('#c43a2f', { roughness: 0.45 }));
  cap.position.set(-25.9, TOP + 3.55, -21.6);
  cap.castShadow = true;
  cap.userData.interactive = { kind: 'press', key: 'reset', label: 'RESET' };
  b.root.add(cap);

  // Cable USB (prop «usb»): conector en la boca del receptáculo tipo B
  const usb = usbCable('B', -38.5, TOP + 5.45, usbZ, [16, 10.9, 12]);
  b.root.add(usb.group, usb.hit);
  usb.group.visible = props.usb !== false;

  // LEDs integrados
  const leds = {};
  const ledDefs = [
    ['L', -6.1, -14.2, '#ffb02e', '#ffa31a'],
    ['TX', -6.1, -11.7, '#ffd84a', '#ffcc1a'],
    ['RX', -6.1, -9.5, '#ffd84a', '#ffcc1a'],
    ['ON', 25.6, -9.6, '#7dff8a', '#36ff4a'],
  ];
  for (const [name, x, z, base, g] of ledDefs) {
    const m = emissiveMat('#f2efe6', g, { roughness: 0.3 });
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.8, 1.25), m);
    mesh.position.set(x, TOP + 0.4, z);
    b.root.add(mesh);
    const halo = glow(g, 9);
    halo.position.set(x, TOP + 1.6, z);
    b.root.add(halo);
    leds[name] = { m, halo, base };
  }

  const view = {
    root: b.root,
    update(v) {
      const set = (name, on) => {
        const l = leds[name];
        setEmissive(l.m, on, 14);
        l.m.color.set(on > 0.05 ? l.base : '#f2efe6');
        setGlow(l.halo, on * 0.9);
      };
      set('L', v.L || 0);
      set('TX', v.TX ? 1 : 0);
      set('RX', v.RX ? 1 : 0);
      set('ON', 0.8 * (v.ON === true ? 1 : Number(v.ON) || 0));
    },
    reset() {
      view.update({ L: 0, TX: false, RX: false, ON: false });
    },
    press(key, down) {
      if (key === 'reset') cap.position.y = TOP + (down ? 3.05 : 3.55);
    },
    setProps(p) {
      usb.group.visible = p.usb !== false;
    },
  };
  view.reset();
  return view;
}
